import { Box, Typography, Button } from "@mui/material";
import mainImg from "../../../assets/images/main-img.png";
import brainIcon from "../../../assets/images/brain-icon.svg";
import timeIcon from "../../../assets/images/time-icon.svg";
import progressIcon from "../../../assets/images/progress-icon.svg";
import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import useUserStore from "../../../store/useUserStore";
import { useImageHeightStore } from "../../../store/useImagesHeight";

gsap.registerPlugin(ScrollTrigger);

const caseBtnStyle = {
  width: "100%",
  height: "64px",
  borderRadius: "60px",
  border: "1px solid rgba(11, 25, 62, 1)",
  backgroundColor: "rgba(250, 253, 255, 1)",
  color: "rgba(11, 25, 62, 1)",
  textTransform: "none",
  fontSize: "20px",
  fontWeight: "500",
  "@media (min-width: 1200px)": {
    width: "289px",
  },
};

const AdviceBtnStyle = {
  width: "100%",
  height: "64px",
  borderRadius: "60px",
  backgroundColor: "rgba(11, 25, 62, 1)",
  color: "rgba(250, 253, 255, 1)",
  textTransform: "none",
  fontSize: "20px",
  fontWeight: "500",
  "@media (min-width: 1200px)": {
    width: "289px",
  },
};

const benefitItemStyle = {
  width: "100%",
  height: "77px",
  position: "absolute",
  backgroundColor: "rgba(255, 255, 255, 0.6)",
  backdropFilter: "blur(24px)",
  border: "1px solid rgba(255, 255, 255, 0.8)",
  borderRadius: "50.58px",
  display: "flex",
  alignItems: "center",
  padding: "0 20px",
  gap: "12px",
  zIndex: 11,
  "@media (min-width: 1200px)": {
    width: "286px",
  },
};

const benefits = [
  {
    icon: timeIcon,
    title: "Швидкість",
    subtitle: "Відповімо в 24 години",
  },
  {
    icon: progressIcon,
    title: "Успішність",
    subtitle: "95% справ виграшні",
  },
  {
    icon: brainIcon,
    title: "Зрозумілість",
    subtitle: "Роз’яснимо всі деталі",
  },
];

export default function MainBlock({ scrollToRef, onScrollTo }) {
  const windowInnerWidth = useUserStore((state) => state.windowInnerWidth);
  const setImageHeight = useImageHeightStore((state) => state.setImageHeight);
  const sectionRef = useRef(null);
  const circleRef = useRef(null);
  const benefitDesktopRefs = useRef([]);
  const benefitMobileRefs = useRef([]);

  useGSAP(() => {
    if (!sectionRef.current || !circleRef.current) return;

    gsap.to(circleRef.current, {
      y: 100,
      ease: "none",
      scrollTrigger: {
        trigger: sectionRef.current,
        start: "top top",
        end: "bottom center",
        scrub: true,
        invalidateOnRefresh: true,
      },
    });

    const refsToAnimate =
      windowInnerWidth >= 1200
        ? benefitDesktopRefs.current
        : benefitMobileRefs.current;

    refsToAnimate.forEach((ref) => {
      if (!ref) return;
      gsap.to(ref, {
        y: -100,
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: "bottom top+=100",
          scrub: true,
          invalidateOnRefresh: true,
        },
      });
    });
  }, [windowInnerWidth]);

  return (
    <Box
      ref={sectionRef}
      sx={{
        width: "100%",
        padding: windowInnerWidth > 960 ? "0 80px" : 0,
        display: "flex",
        justifyContent: "center",
        paddingTop: windowInnerWidth > 1200 ? "185px" : "60px",
      }}
    >
      <Box
        ref={scrollToRef}
        sx={{
          width: "100%",
          maxWidth: "1280px",
          height: "auto",
          display: "flex",
          flexDirection: "column-reverse",
          "@media (min-width: 1200px)": {
            flexDirection: "row",
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            px: windowInnerWidth < 640 ? "20px" : 0,
          }}
        >
          <Box width={"100%"}>
            <Typography
              variant="h1"
              sx={{
                fontSize: "32px",
                fontWeight: 400,
                color: "rgba(11, 25, 62, 1)",
                "@media (min-width: 640px)": {
                  fontSize: "48px",
                },
                "@media (min-width: 960px)": {
                  fontSize: "56px",
                },
                "@media (min-width: 1200px)": {
                  fontSize: "62px",
                },
              }}
            >
              Юридичні рішення <br /> для{" "}
              <b>
                IT-компаній <br />
                та стартапів
              </b>
            </Typography>
            <Typography
              sx={{
                mt: "16px",
                fontSize: "20px",
                fontWeight: 400,
                "@media (min-width: 1200px)": {
                  mt: "29px",
                  fontSize: "24px",
                },
              }}
            >
              Контракти, IP, компанії, податки <br /> – з гарантією результату
            </Typography>
          </Box>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "41px",
              mt: "43px",
              "@media (min-width: 1200px)": {
                flexDirection: "row",
                mt: 0,
              },
            }}
          >
            <Button sx={caseBtnStyle} onClick={onScrollTo.feedbacks}>
              Переглянути кейси
            </Button>
            <Button sx={AdviceBtnStyle} onClick={() => window.open('https://calendly.com/kostiantinople/30min?month=2025-09', '_blank')}>
              Отримати консультацію
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            width: "100%",
            position: "relative",
            "@media (min-width: 1200px)": {
              maxWidth: "500px",
            },
          }}
        >
          <Box
            ref={circleRef}
            sx={{
              position: "absolute",
              top: "-80px",
              right: "0",
              width: "264px",
              height: "264px",
              borderRadius: "50%",
              backgroundColor: "rgba(11, 25, 62, 0.5)",
              filter: "drop-shadow(0px 0px 10px rgba(11, 25, 62, 0.5))",
              zIndex: 5,
            }}
          />
          <Box
            component={"img"}
            src={mainImg}
            alt="main image"
            onLoad={(e) => {
              const height = e.target.clientHeight;
              setImageHeight("main-img", height);
            }}
            sx={{
              width: "100%",
              zIndex: 10,
              position: "relative",
              "@media (min-width: 1200px)": {
                height: "547px",
              },
            }}
          />
          {benefits.map((benefit, index) => {
            let benefitPosition = {};
            if (index === 0) {
              benefitPosition = { top: "177px", left: "-140px" };
            } else if (index === 1) {
              benefitPosition = { top: "364px", left: "-140px" };
            } else if (index === 2) {
              benefitPosition = { bottom: 0, right: 0 };
            }
            return (
              <Box
                key={`desktop-${index}`}
                ref={(el) => (benefitDesktopRefs.current[index] = el)}
                sx={{
                  ...benefitItemStyle,
                  ...benefitPosition,
                  display: { xs: "none", lg: "flex" },
                }}
              >
                <Box
                  component="img"
                  src={benefit.icon}
                  alt={`${benefit.title} icon`}
                  sx={{ width: "40px", height: "40px" }}
                />
                <Box>
                  <Typography sx={{ fontSize: "20px", fontWeight: 600 }}>
                    {benefit.title}
                  </Typography>
                  <Typography sx={{ fontSize: "16px", fontWeight: 400 }}>
                    {benefit.subtitle}
                  </Typography>
                </Box>
              </Box>
            );
          })}

          <Box
            sx={{
              display: { xs: "flex", lg: "none" },
              flexDirection: "column",
              gap: "16px",
              mt: "-40px",
              mb: "88px",
              px: windowInnerWidth < 640 ? "20px" : 0,
              "@media (min-width: 1200px)": {
                mb: 0,
              },
            }}
          >
            {benefits.map((benefit, index) => (
              <Box
                key={`mobile-${index}`}
                ref={(el) => (benefitMobileRefs.current[index] = el)}
                sx={{
                  ...benefitItemStyle,
                  position: "relative",
                }}
              >
                <Box
                  component="img"
                  src={benefit.icon}
                  alt={`${benefit.title} icon`}
                  sx={{ width: "40px", height: "40px" }}
                />
                <Box>
                  <Typography sx={{ fontSize: "20px", fontWeight: 600 }}>
                    {benefit.title}
                  </Typography>
                  <Typography sx={{ fontSize: "16px", fontWeight: 400 }}>
                    {benefit.subtitle}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
